import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Link, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import LinkedInPrompt from './components/LinkedInPrompt';
import Dashboard from './components/Dashboard';
import ConnectionsDashboard from './components/ConnectionsDashboard';
import JobSearchDashboard from './components/JobSearchDashboard';
import JobsForYouPage from './components/JobsForYouPage';
import PendingApprovalPage from './components/PendingApprovalPage';
import './index.css';

// Loading component
const LoadingSpinner: React.FC = () => (
  <div className="min-h-screen bg-ts-cream flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ts-gold mx-auto mb-4"></div>
      <p className="text-gray-500">Loading...</p>
    </div>
  </div>
);

// Main app content component with routing
const AppContent: React.FC = () => {
  const { user, userProfile, loading, accessState, accessMessage, isAdmin } = useAuth();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState<'linkedin' | 'dashboard'>('linkedin');

  useEffect(() => {
    if (userProfile) {
      if (!userProfile.hasBeenPromptedForLinkedIn) {
        setCurrentStep('linkedin');
      } else {
        setCurrentStep('dashboard');
      }
    }
  }, [userProfile]);

  if (loading) return <LoadingSpinner />;
  if (!user) return <LoginPage />;
  if (accessState === 'unknown') return <LoadingSpinner />;
  if (accessState !== 'allowed') return <PendingApprovalPage status={accessState} message={accessMessage} />;

  if (currentStep === 'linkedin' && !userProfile?.hasBeenPromptedForLinkedIn && location.pathname !== '/connections') {
    return <LinkedInPrompt onComplete={() => setCurrentStep('dashboard')} />;
  }

  return (
    <Routes>
      <Route path="/connections" element={<ConnectionsDashboard />} />
      <Route path="/jobs-for-you" element={<JobsForYouPage />} />
      <Route path="/match-jobs" element={<Navigate to="/jobs-for-you?step=3" replace />} />
      {/* Fetch Additional Jobs — admin only; redirect regular users home */}
      <Route
        path="/job-finder"
        element={isAdmin ? <JobSearchDashboard /> : <Navigate to="/" replace />}
      />
      <Route path="/" element={<Dashboard />} />
    </Routes>
  );
};

// Header component for authenticated users
const Header: React.FC = () => {
  const { user, userProfile, signOut, accessState, isAdmin } = useAuth();
  const location = useLocation();

  if (!user) return null;
  const showNavigation = accessState === 'allowed';

  return (
    <header className="bg-ts-cream border-b border-ts-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-8">
            {/* TopSeeds wordmark */}
            <Link to="/" className="flex items-center">
              <span className="font-serif text-xl font-semibold tracking-wide">
                <span className="text-ts-charcoal">Top</span><span className="text-ts-gold">Seeds</span>
              </span>
            </Link>
            {showNavigation && (
              <nav className="flex space-x-6">
                <Link
                  to="/"
                  className={`text-sm font-medium transition-colors ${
                    location.pathname === '/'
                      ? 'text-ts-gold'
                      : 'text-ts-charcoal/60 hover:text-ts-charcoal'
                  }`}
                >
                  Dashboard
                </Link>
                <Link
                  to="/connections"
                  className={`text-sm font-medium transition-colors ${
                    location.pathname === '/connections'
                      ? 'text-ts-gold'
                      : 'text-ts-charcoal/60 hover:text-ts-charcoal'
                  }`}
                >
                  Connections
                </Link>
                <Link
                  to="/jobs-for-you"
                  className={`text-sm font-medium transition-colors ${
                    location.pathname === '/jobs-for-you' || location.pathname === '/match-jobs'
                      ? 'text-ts-gold'
                      : 'text-ts-charcoal/60 hover:text-ts-charcoal'
                  }`}
                >
                  Recommended Jobs
                </Link>
                {/* Fetch Additional Jobs — admin only */}
                {isAdmin && (
                  <Link
                    to="/job-finder"
                    className={`text-sm font-medium transition-colors ${
                      location.pathname === '/job-finder'
                        ? 'text-ts-gold'
                        : 'text-ts-charcoal/60 hover:text-ts-charcoal'
                    }`}
                  >
                    Job Finder
                  </Link>
                )}
              </nav>
            )}
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              {/* Admin badge */}
              {isAdmin && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-ts-gold/10 text-ts-gold border border-ts-gold/30">
                  Admin
                </span>
              )}
              <div className="h-8 w-8 bg-ts-navy rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {userProfile
                    ? userProfile.firstName.charAt(0) + userProfile.lastName.charAt(0)
                    : user.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="text-sm font-medium text-ts-charcoal">
                {userProfile ? `${userProfile.firstName} ${userProfile.lastName}` : user.email}
              </span>
            </div>

            <button
              onClick={signOut}
              className="text-sm text-gray-400 hover:text-ts-charcoal transition-colors duration-200"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

// Main App component
const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <div className="App min-h-screen bg-ts-cream">
          <Header />
          <AppContent />
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;